import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { SEO, SITE } from "@/config/site";
import { Lines, Fade, Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import { BookingSection } from "@/components/CTA";
import { trackEvent } from "@/lib/analytics";

const PILLARS = ["Nutrizione", "Movimento", "Scienza", "Personalizzazione"];

const SERVICES = [
  {
    number: "01",
    title: "Nutrizione",
    text: "Percorsi nutrizionali personalizzati costruiti su obiettivi, abitudini alimentari, stile di vita, esigenze individuali, attività fisica e preferenze personali.",
    areas: [
      "Educazione alimentare",
      "Dimagrimento",
      "Ricomposizione corporea",
      "Nutrizione sportiva",
      "Abitudini alimentari sostenibili",
      "Organizzazione dei pasti",
    ],
    cta: { label: "Scopri Nutrizione", to: "/nutrizione" },
    accent: "text-leaf-deep",
  },
  {
    number: "02",
    title: "Allenamento",
    text: "Programmazione dell'esercizio fisico su misura, definita in base a obiettivi, livello attuale, esperienza, tempo e attrezzatura disponibili e caratteristiche individuali.",
    areas: [
      "Allenamento personalizzato",
      "Forza",
      "Corpo libero",
      "Composizione corporea",
      "Salute e benessere",
      "Casa e palestra",
      "Allenamento in acqua e programmazione del nuoto",
    ],
    cta: { label: "Scopri Allenamento", to: "/allenamento" },
    accent: "text-wave-deep",
  },
  {
    number: "03",
    title: "Approccio integrato",
    text: "Quando è utile, nutrizione e allenamento possono essere coordinati in un unico percorso. Ogni area resta indipendente: l'integrazione è un'opportunità, mai un vincolo.",
    quote: "Due aree, un'unica visione della persona.",
    cta: { label: "Prenota una visita", to: "/contatti" },
    accent: "text-ink",
  },
];

const METHOD = [
  {
    number: "01",
    title: "Valutazione",
    text: "Comprendere la persona, le abitudini, le necessità, gli obiettivi e raccogliere le misurazioni utili. Ogni percorso parte dall'ascolto e da una valutazione attenta.",
  },
  {
    number: "02",
    title: "Personalizzazione",
    text: "Costruire il percorso intorno alla persona, non adattare la persona a un protocollo standard.",
  },
  {
    number: "03",
    title: "Evidenza scientifica",
    text: "Utilizzare le conoscenze scientifiche come base di ogni scelta professionale.",
  },
  {
    number: "04",
    title: "Monitoraggio",
    text: "Valutare progressi, difficoltà e risposta al percorso nel tempo, con attenzione e continuità.",
  },
  {
    number: "05",
    title: "Adattamento",
    text: "Modificare la strategia quando necessario, perché un percorso efficace è un percorso che evolve.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 70]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-36">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-24 h-[30rem] w-[30rem] rounded-full bg-leaf/10 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 pb-20 sm:pb-28 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-10">
        <div>
          <Fade delay={0.1}>
            <p className="eyebrow text-leaf-deep" data-testid="hero-eyebrow">
              Nutrimove Lab · Bologna e Casalecchio di Reno
            </p>
          </Fade>
          <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-balance">
            <Lines
              delay={0.25}
              lines={[
                "Nutrizione e allenamento",
                <span key="l2">
                  basati sulla <em className="text-wave-deep">scienza</em>
                </span>,
              ]}
            />
          </h1>
          <Fade delay={0.75}>
            <p className="mt-7 max-w-md text-base md:text-lg leading-relaxed text-ink/65">
              Percorsi personalizzati per migliorare alimentazione, composizione corporea, salute e
              performance.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <span aria-hidden="true" className="h-px w-10 bg-leaf" />
              <p className="text-sm leading-snug">
                <strong className="font-semibold">{SITE.professional.name}</strong>
                <br />
                <span className="text-ink/55">{SITE.professional.role}</span>
              </p>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-booking-cta"
                onClick={() => trackEvent("booking_click", { cta: "hero" })}
                className="group inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-leaf-deep"
              >
                Prenota una visita
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#metodo"
                data-testid="hero-approach-cta"
                className="group inline-flex items-center gap-2 py-4 text-sm font-semibold text-ink/70 transition-colors duration-200 hover:text-leaf-deep"
              >
                Scopri il mio approccio
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Fade>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div
            aria-hidden="true"
            className="absolute -right-8 -top-8 h-28 w-28 rounded-full border border-wave/40"
          />
          <motion.div style={{ y: photoY }} className="relative">
            <img
              src={SITE.images.homeHero}
              alt="Nutrizione e movimento insieme: alimenti freschi, attività fisica e attrezzatura per l'allenamento — la visione di Nutrimove Lab"
              className="w-full [filter:drop-shadow(0_28px_36px_rgba(16,22,20,0.14))]"
              width={1100}
              height={1375}
              fetchPriority="high"
              data-testid="hero-visual"
            />
          </motion.div>
          <div className="absolute bottom-5 left-5 right-5 flex items-center gap-4 rounded-2xl border border-ink/10 bg-white/90 px-5 py-4 backdrop-blur-md">
            <img
              src={SITE.images.logo}
              alt=""
              aria-hidden="true"
              className="h-10 w-10 object-contain"
              width={40}
              height={40}
            />
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/70">
              Alimentazione · Salute · Movimento
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const TrustStrip = () => (
  <section aria-label="I pilastri dell'approccio" className="border-y border-ink/10 bg-mist">
    <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
      {PILLARS.map((pillar, i) => (
        <Reveal key={pillar} delay={i * 0.08} className="border-ink/10">
          <div
            className={`flex items-baseline gap-3 px-4 py-7 sm:gap-4 sm:px-10 sm:py-10 ${
              i > 0 ? "border-l border-ink/10" : ""
            } ${i > 1 ? "border-t lg:border-t-0" : ""} ${i === 2 ? "border-l-0 lg:border-l" : ""}`}
          >
            <span className="font-display italic text-sm text-leaf-deep">0{i + 1}</span>
            <span className="text-[10px] sm:text-sm font-bold uppercase tracking-[0.14em] sm:tracking-[0.22em] text-ink/80">
              {pillar}
            </span>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Services = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" aria-label="Aree di lavoro">
    <Reveal>
      <p className="eyebrow text-wave-deep">Cosa faccio</p>
      <h2 className="mt-6 max-w-2xl font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
        Due competenze, un unico approccio: salute, composizione corporea, benessere e performance.
      </h2>
    </Reveal>

    <div className="mt-16 sm:mt-20">
      {SERVICES.map((service, i) => (
        <Reveal key={service.number} delay={0.05}>
          <article
            className={`grid gap-8 border-t border-ink/10 py-12 sm:py-16 lg:grid-cols-[auto_1fr_1.2fr] lg:gap-16 ${
              i === SERVICES.length - 1 ? "border-b" : ""
            }`}
          >
            <span className={`font-display italic text-4xl sm:text-5xl ${service.accent}`}>
              {service.number}
            </span>
            <div>
              <h3 className="font-display text-3xl sm:text-4xl tracking-tight">{service.title}</h3>
              {service.quote && (
                <p className="mt-4 font-display italic text-lg text-ink/55">“{service.quote}”</p>
              )}
              <Link
                to={service.cta.to}
                data-testid={`service-cta-${service.number}`}
                className="group mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:text-leaf-deep"
              >
                {service.cta.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            <div>
              <p className="text-base leading-relaxed text-ink/65">{service.text}</p>
              {service.areas && (
                <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
                  {service.areas.map((area) => (
                    <li key={area} className="flex items-center gap-3 text-sm text-ink/70">
                      <span aria-hidden="true" className="h-1 w-1 rounded-full bg-leaf" />
                      {area}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  </section>
);

const Method = () => (
  <section id="metodo" className="bg-mist" aria-label="Il metodo">
    <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 py-24 sm:py-32 lg:grid-cols-[1fr_1.4fr] lg:gap-24">
      <div className="lg:sticky lg:top-32 lg:self-start">
        <Reveal>
          <p className="eyebrow text-leaf-deep">Il metodo</p>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-balance">
            Un metodo scientifico, pratico e personalizzato
          </h2>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-ink/60">
            Cinque principi che guidano ogni percorso, dal primo incontro ai risultati nel tempo.
          </p>
        </Reveal>
      </div>
      <ol className="flex flex-col">
        {METHOD.map((step, i) => (
          <Reveal key={step.number} delay={0.04 * i}>
            <li className="group grid grid-cols-[auto_1fr] items-baseline gap-6 border-t border-ink/10 py-9 sm:gap-12 sm:py-11">
              <span className="font-display italic text-2xl text-ink/30 transition-colors duration-300 group-hover:text-leaf-deep sm:text-3xl">
                {step.number}
              </span>
              <div>
                <h3 className="text-base font-bold uppercase tracking-[0.18em]">{step.title}</h3>
                <p className="mt-3 max-w-lg text-sm sm:text-base leading-relaxed text-ink/60">
                  {step.text}
                </p>
              </div>
            </li>
          </Reveal>
        ))}
        <div className="border-t border-ink/10" />
      </ol>
    </div>
  </section>
);

const Professional = () => (
  <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32" aria-label="Il professionista">
    <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
      <Reveal className="relative order-2 lg:order-1">
        <img
          src={SITE.images.portraitHome}
          alt="Dott. Michele Ceccarelli, Biologo Nutrizionista e Dott. in Scienze Motorie — Nutrimove Lab"
          className="mx-auto w-full max-w-[300px] sm:max-w-sm [filter:drop-shadow(0_24px_32px_rgba(16,22,20,0.14))]"
          width={1100}
          height={1135}
          loading="lazy"
          data-testid="professional-portrait"
        />
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal>
          <p className="eyebrow text-wave-deep">Il professionista</p>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight">
            {SITE.professional.name}
          </h2>
          <p className="mt-4 text-sm font-bold uppercase tracking-[0.2em] text-leaf-deep">
            {SITE.professional.role}
          </p>
          <p className="mt-7 max-w-lg text-base md:text-lg leading-relaxed text-ink/65">
            Nutrizione e movimento sono due aree complementari della stessa visione: comprendere la
            persona nel suo insieme e costruire percorsi basati sulla scienza, sostenibili nella
            vita di tutti i giorni.
          </p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-9 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-ink/10 bg-mist px-5 py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/45">
                Formazione accademica
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/70">
                Scienze delle Attività Motorie e Sportive — Università di Bologna
                <br />
                Scienze dell'Alimentazione — Università di Firenze
              </p>
            </div>
            <div className="rounded-xl border border-ink/10 bg-mist px-5 py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/45">
                Iscrizione professionale
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-ink/70">
                Ordine dei Biologi dell'Emilia-Romagna e delle Marche
                <br />
                Sezione A, n. 4982
              </p>
            </div>
          </div>
          <Link
            to="/chi-sono"
            data-testid="professional-cta"
            className="group mt-9 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-200 hover:text-leaf-deep"
          >
            Scopri chi sono
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Reviews = () => (
  <section className="border-t border-ink/10 bg-mist" aria-label="Recensioni dei pazienti">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
      <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-center">
        <Reveal>
          <p className="eyebrow text-leaf-deep">Recensioni</p>
          <h2 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance">
            Cosa dicono i pazienti
          </h2>
          <p className="mt-6 max-w-lg text-base md:text-lg leading-relaxed text-ink/60">
            Chi ha già iniziato un percorso racconta la propria esperienza su Google e su
            MioDottore: recensioni reali, con nome e valutazione verificabili.
          </p>
        </Reveal>
        <Reveal delay={0.12} className="lg:justify-self-end">
          <Link
            to="/recensioni"
            data-testid="reviews-teaser-cta"
            className="group inline-flex items-center gap-3 rounded-full border border-ink/20 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-ink transition-colors duration-300 hover:border-leaf-deep hover:text-leaf-deep"
          >
            Leggi le recensioni
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  useSEO(SEO.home);
  return (
    <>
      <Hero />
      <TrustStrip />
      <Marquee />
      <Services />
      <Method />
      <Professional />
      <Reviews />
      <BookingSection />
    </>
  );
}
